import { Throwable } from "@wss/error-tracking/throwable";
import { toError } from "@wss/error-tracking/utils";
import { ExtendedMediaConstraints } from ".";

export interface CropBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Wrapper for `navigator.mediaDevices.getUserMedia`
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia Documentation on MDN}
 */
export async function getUserMedia(): Promise<Throwable<MediaStream>> {
  if (!("mediaDevices" in navigator)) {
    const error = new Error(
      "Navigator does not have mediaDevices. IE is not supported.",
    );
    return { isError: true, error };
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: "environment",
      },
    });
    return { isError: false, value: stream };
  } catch (error) {
    return { isError: true, error: toError(error) };
  }
}

export async function getMediaConstraints(): Promise<
  Throwable<ExtendedMediaConstraints>
> {
  if (!("mediaDevices" in navigator)) {
    const error = new Error(
      "Navigator does not have mediaDevices. IE is not supported.",
    );
    return { isError: true, error };
  }
  try {
    const constraints = navigator.mediaDevices.getSupportedConstraints();

    return { isError: false, value: constraints as ExtendedMediaConstraints };
  } catch (error) {
    return { isError: true, error: toError(error) };
  }
}

/**
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toBlob Documentation on MDN}
 */
export async function getBlobFromCanvas(
  canvas: HTMLCanvasElement,
): Promise<Throwable<Blob>> {
  return new Promise(function handlePromise(resolve) {
    try {
      return canvas.toBlob(function handleBlob(blob) {
        if (!blob) {
          const error = new Error(
            "toBlob was unable to create blob from video stream",
          );
          return resolve({ isError: true, error });
        }
        return resolve({ isError: false, value: blob });
      }, "image/jpeg");
    } catch (error) {
      return resolve({
        isError: true,
        error: toError(error),
      });
    }
  });
}

export const getBlobFromImageURL = async (
  imageUrl: string,
): Promise<Throwable<Blob>> => {
  try {
    const url = new URL(imageUrl);
    const result = await fetch(url);
    const blob = await result.blob();

    return {
      isError: false,
      value: blob,
    };
  } catch (error) {
    return {
      isError: true,
      error: toError(error),
    };
  }
};

export async function resizeImage(
  imageUrl: string,
  width?: number,
  height?: number,
  cropBox?: CropBox,
): Promise<Throwable<Blob>> {
  try {
    const maybeImage = await handleLoadImageFromSrc(imageUrl);
    if (maybeImage.isError) return maybeImage;
    const canvas = document.createElement("canvas");
    canvas.width = width ?? maybeImage.value.width;
    canvas.height = height ?? maybeImage.value.height;
    const ctx = canvas.getContext("2d");
    if (cropBox) {
      ctx?.drawImage(
        maybeImage.value,
        cropBox.x,
        cropBox.y,
        cropBox.width,
        cropBox.height,
        0,
        0,
        width ?? maybeImage.value.width,
        height ?? maybeImage.value.height,
      );
    } else {
      ctx?.drawImage(maybeImage.value, 0, 0);
    }
    const resizedBlob = await getBlobFromCanvas(canvas);
    if (resizedBlob.isError) {
      return {
        isError: true,
        error: new Error("Unable to resize image"),
      };
    }
    return { isError: false, value: resizedBlob.value };
  } catch (error) {
    return {
      isError: true,
      error: toError(error),
    };
  }
}

export async function resizeBlob(
  blob: Blob,
  width?: number,
  height?: number,
  cropBox?: CropBox,
): Promise<Throwable<Blob>> {
  const imageUrl = URL.createObjectURL(blob);
  const resizedBlob = await resizeImage(imageUrl, width, height, cropBox);
  URL.revokeObjectURL(imageUrl);
  return resizedBlob;
}

export async function handleLoadImageFromSrc(
  src: string,
): Promise<Throwable<InstanceType<typeof Image>>> {
  return new Promise((resolve) => {
    const img = new Image();
    img.addEventListener("load", function handleImageLoad() {
      return resolve({ isError: false, value: img });
    });
    img.addEventListener("error", (error) => {
      return { isError: true, error: new Error(error.message) };
    });
    img.src = src;
  });
}
